import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Save, Trash2, Plus, GripVertical, FlaskConical, Calculator, EyeOff } from "lucide-react";
import { toast } from "sonner";
import type { Question, QuestionAlternative } from "@/lib/api_client";
import { QUESTION_KIND_LABELS, ADVANCED_TYPE_LABELS } from "@/lib/types";
import type { QuestionKind, AdvancedQuestionType } from "@/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  useQuestionAlternatives, useUpdateQuestion, useDeleteQuestion,
  useCreateQuestionAlternative, useUpdateQuestionAlternative, useDeleteQuestionAlternative,
  useQuestionNumericRules, useCreateQuestionNumericRule, useUpdateQuestionNumericRule, useDeleteQuestionNumericRule,
  useBiometrics,
} from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BiometricPicker } from "@/components/shared/biometric-picker";

function toStringArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.filter((v): v is string => typeof v === "string");
  return [];
}

const ARITHMETIC_LABELS: Record<string, string> = {
  equal: "=",
  greater_than: ">",
  less_than: "<",
};

type AltEdit = QuestionAlternative & { dirty?: boolean; isNew?: boolean };
type RuleEdit = {
  id: string;
  arithmetic: "equal" | "less_than" | "greater_than";
  treshold: string;
  recommendedBiometricIds: string[];
  isNew?: boolean;
};

interface QuestionCardProps {
  question: Question;
}

export function QuestionCard({ question }: QuestionCardProps) {
  const qc = useQueryClient();
  const { data: allAlternatives } = useQuestionAlternatives();
  const { data: allBiometrics } = useBiometrics();
  const { data: allNumericRules } = useQuestionNumericRules();
  const updateQuestion = useUpdateQuestion();
  const deleteQuestion = useDeleteQuestion();
  const createAlt = useCreateQuestionAlternative();
  const updateAlt = useUpdateQuestionAlternative();
  const deleteAlt = useDeleteQuestionAlternative();
  const createRule = useCreateQuestionNumericRule();
  const updateRule = useUpdateQuestionNumericRule();
  const deleteRule = useDeleteQuestionNumericRule();

  const alternatives = useMemo(
    () => (allAlternatives ?? []).filter((a) => a.questionId === question.id),
    [allAlternatives, question.id]
  );
  const numericRules = useMemo(
    () => (allNumericRules ?? []).filter((r) => r.questionId === question.id),
    [allNumericRules, question.id]
  );
  const biometrics = allBiometrics ?? [];

  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    question: question.question ?? "",
    description: question.description ?? "",
    nickname: question.nickname ?? "",
    kind: (question.kind ?? "onboarding") as QuestionKind,
    tags: question.tags ?? "",
    advancedQuestionType: (question.advancedQuestionType ?? "single_select") as AdvancedQuestionType,
    secondaryQuestion: question.secondaryQuestion ?? "",
    isNumber: question.isNumber ?? false,
    multipleSelect: question.multipleSelect ?? false,
  });

  const [altEdits, setAltEdits] = useState<AltEdit[]>([]);
  const [ruleEdits, setRuleEdits] = useState<RuleEdit[]>([]);

  const startEditing = () => {
    setForm({
      question: question.question ?? "",
      description: question.description ?? "",
      nickname: question.nickname ?? "",
      kind: (question.kind ?? "onboarding") as QuestionKind,
      tags: question.tags ?? "",
      advancedQuestionType: (question.advancedQuestionType ?? "single_select") as AdvancedQuestionType,
      secondaryQuestion: question.secondaryQuestion ?? "",
      isNumber: question.isNumber ?? false,
      multipleSelect: question.multipleSelect ?? false,
    });
    setAltEdits(alternatives.map((a) => ({ ...a })));
    setRuleEdits(numericRules.map((r) => ({
      id: r.id,
      arithmetic: r.arithmetic,
      treshold: r.treshold,
      recommendedBiometricIds: toStringArray(r.recommendedBiometricIds),
    })));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateQuestion.mutateAsync({
        id: question.id,
        data: { ...form, secondaryQuestion: form.secondaryQuestion || null },
      });

      // Sync alternatives
      const existingAltIds = new Set(alternatives.map((a) => a.id));
      const editAltIds = new Set(altEdits.map((a) => a.id));

      for (const alt of altEdits) {
        const data = {
          text: alt.text,
          order: alt.order,
          isOther: alt.isOther,
          isActive: alt.isActive ?? true,
          recommendedBiometricIds: toStringArray(alt.recommendedBiometricIds),
          followupQuestionIds: toStringArray(alt.followupQuestionIds),
        };
        if (existingAltIds.has(alt.id)) {
          await updateAlt.mutateAsync({ id: alt.id, data });
        } else {
          await createAlt.mutateAsync({ questionId: question.id, ...data });
        }
      }

      // Sync numeric rules — delete all if isNumber was turned off
      const existingRuleIds = new Set(numericRules.map((r) => r.id));
      if (form.isNumber) {
        const editRuleIds = new Set(ruleEdits.map((r) => r.id));
        for (const rule of ruleEdits) {
          const data = {
            arithmetic: rule.arithmetic,
            treshold: rule.treshold,
            recommendedBiometricIds: rule.recommendedBiometricIds,
          };
          if (existingRuleIds.has(rule.id)) {
            await updateRule.mutateAsync({ id: rule.id, data });
          } else {
            await createRule.mutateAsync({ questionId: question.id, ...data });
          }
        }
        for (const rule of numericRules) {
          if (!editRuleIds.has(rule.id)) await deleteRule.mutateAsync(rule.id);
        }
      } else {
        for (const rule of numericRules) {
          await deleteRule.mutateAsync(rule.id);
        }
      }

      setEditing(false);
      toast.success("Sporsmal lagret");
    } catch {
      toast.error("Kunne ikke lagre");
      qc.invalidateQueries({ queryKey: ["question-alternatives"] });
      qc.invalidateQueries({ queryKey: ["question-numeric-rules"] });
      setAltEdits([]);
      setRuleEdits([]);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDeprecated = async () => {
    try {
      await updateQuestion.mutateAsync({ id: question.id, data: { isDeprecated: !question.isDeprecated } });
      toast.success(question.isDeprecated ? "Spørsmål gjenopprettet" : "Spørsmål markert som utgått");
    } catch {
      toast.error("Kunne ikke lagre");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteQuestion.mutateAsync(question.id);
      toast.success("Sporsmal slettet");
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  // Alternative helpers
  const handleAddAlternative = () => {
    setAltEdits((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(), questionId: question.id, text: "", order: String(prev.length + 1),
        isOther: false, followupQuestionIds: null, recommendedBiometricIds: null,
        createdAt: "", updatedAt: "", isNew: true,
      } as AltEdit,
    ]);
  };

  const updateAltField = (idx: number, field: string, value: unknown) => {
    setAltEdits((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value, dirty: true } : a)));
  };

  const removeAltEdit = (idx: number) => {
    setAltEdits((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleToggleActive = async (alt: AltEdit, idx: number) => {
    const newActive = !(alt.isActive ?? true);
    setAltEdits((prev) => prev.map((a, i) => (i === idx ? { ...a, isActive: newActive } : a)));
    if (!alt.isNew) {
      await updateAlt.mutateAsync({ id: alt.id, data: { isActive: newActive } });
    }
  };

  // Numeric rule helpers
  const handleAddRule = () => {
    setRuleEdits((prev) => [
      ...prev,
      { id: crypto.randomUUID(), arithmetic: "greater_than" as const, treshold: "0", recommendedBiometricIds: [], isNew: true },
    ]);
  };

  const updateRuleField = (idx: number, field: string, value: unknown) => {
    setRuleEdits((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const removeRule = (idx: number) => {
    setRuleEdits((prev) => prev.filter((_, i) => i !== idx));
  };

  // Helpers for biometric name lookup
  const biometricName = (id: string) => {
    const match = biometrics.find((bm) => bm.id === id);
    return match?.name || match?.shortName || id.slice(0, 8);
  };

  const kindLabel = QUESTION_KIND_LABELS[(question.kind ?? "onboarding") as QuestionKind];
  const typeLabel = question.advancedQuestionType ? ADVANCED_TYPE_LABELS[question.advancedQuestionType as AdvancedQuestionType] : null;

  // Count recommended biometrics across alternatives for the header badge
  const totalAltBiometrics = alternatives.reduce((n, a) => n + toStringArray(a.recommendedBiometricIds).length, 0);

  return (
    <Card>
      <CardContent className="p-0">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-3 p-3 text-left hover:bg-admin-slate-hover transition-colors cursor-pointer"
        >
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0 text-admin-text-subtle" /> : <ChevronRight className="h-4 w-4 shrink-0 text-admin-text-subtle" />}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${question.isDeprecated ? "line-through text-admin-text-subtle" : ""}`}>{question.question}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {question.isDeprecated && (
                <Badge variant="outline" className="text-[10px] text-admin-text-subtle" title="Vises bare for kunder som allerede har svart">
                  Utgått
                </Badge>
              )}
              {kindLabel && <Badge variant="outline" className="text-[10px]">{kindLabel}</Badge>}
              {typeLabel && <Badge variant="secondary" className="text-[10px]">{typeLabel}</Badge>}
              {question.isNumber && <Badge variant="secondary" className="text-[10px]">Numerisk</Badge>}
              <span className="text-xs text-admin-text-subtle">#{question.sequenceNumber}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {totalAltBiometrics > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-admin-primary">
                <FlaskConical className="h-3 w-3" />{totalAltBiometrics}
              </span>
            )}
            {numericRules.length > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-admin-info">
                <Calculator className="h-3 w-3" />{numericRules.length}
              </span>
            )}
            <span className="text-xs text-admin-text-subtle">{alternatives.length} alt.</span>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-admin-card-border p-3 space-y-3">
            {editing ? (
              <>
                {/* Question fields */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-admin-text-subtle mb-1">Sporsmal</label>
                    <Textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="min-h-[60px]" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-admin-text-subtle mb-1">Beskrivelse</label>
                    <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Kallenavn</label>
                      <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Type</label>
                      <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as QuestionKind })}>
                        {(Object.keys(QUESTION_KIND_LABELS) as QuestionKind[]).map((k) => (
                          <option key={k} value={k}>{QUESTION_KIND_LABELS[k]}</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Avansert</label>
                      <Select
                        value={form.advancedQuestionType ?? ""}
                        onChange={(e) => setForm({ ...form, advancedQuestionType: (e.target.value || null) as AdvancedQuestionType | null })}
                      >
                        <option value="">Ingen</option>
                        {(Object.keys(ADVANCED_TYPE_LABELS) as AdvancedQuestionType[]).map((t) => (
                          <option key={t} value={t}>{ADVANCED_TYPE_LABELS[t]}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  {form.advancedQuestionType === "double_question" && (
                    <div>
                      <label className="block text-xs font-medium text-admin-text-subtle mb-1">Sekundarsporsmal</label>
                      <Input value={form.secondaryQuestion} onChange={(e) => setForm({ ...form, secondaryQuestion: e.target.value })} />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-admin-text-subtle mb-1">Tags</label>
                    <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="kommaseparert" />
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.isNumber} onChange={(e) => setForm({ ...form, isNumber: e.target.checked })} className="accent-admin-primary" />
                      Numerisk svar
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.multipleSelect} onChange={(e) => setForm({ ...form, multipleSelect: e.target.checked })} className="accent-admin-primary" />
                      Flervalg
                    </label>
                  </div>
                </div>

                {/* Numeric rules (only when isNumber) */}
                {form.isNumber && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-admin-text-subtle flex items-center gap-1.5">
                        <Calculator className="h-3.5 w-3.5" />
                        Numeriske regler
                      </label>
                      <Button variant="outline" size="sm" onClick={handleAddRule} className="h-6 text-xs">
                        <Plus className="h-3 w-3" />
                        Legg til regel
                      </Button>
                    </div>
                    {ruleEdits.map((rule, idx) => (
                      <div key={rule.id} className="rounded border border-admin-card-border p-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-admin-text-subtle font-medium shrink-0">Regel {idx + 1}</span>
                          <Select
                            value={rule.arithmetic}
                            onChange={(e) => updateRuleField(idx, "arithmetic", e.target.value)}
                            className="w-20 h-7 text-sm"
                          >
                            <option value="equal">=</option>
                            <option value="greater_than">&gt;</option>
                            <option value="less_than">&lt;</option>
                          </Select>
                          <Input
                            type="number"
                            value={rule.treshold}
                            onChange={(e) => updateRuleField(idx, "treshold", e.target.value)}
                            onBlur={() => {
                              if (rule.treshold === "" || Number.isNaN(Number(rule.treshold))) {
                                updateRuleField(idx, "treshold", "0");
                              }
                            }}
                            placeholder="Terskel"
                            className="w-24 h-7 text-sm"
                          />
                          <Button variant="ghost" size="icon" onClick={() => removeRule(idx)} className="h-7 w-7 text-admin-negative hover:text-admin-negative shrink-0 ml-auto">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <BiometricPicker
                          selected={rule.recommendedBiometricIds}
                          onChange={(ids) => updateRuleField(idx, "recommendedBiometricIds", ids)}
                          biometrics={biometrics}
                        />
                      </div>
                    ))}
                    {ruleEdits.length === 0 && (
                      <p className="text-xs text-admin-text-subtle pl-1">Ingen regler lagt til</p>
                    )}
                  </div>
                )}

                {/* Alternatives */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-admin-text-subtle">Alternativer</label>
                    <Button variant="outline" size="sm" onClick={handleAddAlternative} className="h-6 text-xs">
                      <Plus className="h-3 w-3" />
                      Legg til
                    </Button>
                  </div>
                  {altEdits.map((alt, idx) => (
                    <div key={alt.id} className={`rounded border p-2 space-y-2 ${alt.isActive === false ? "border-admin-card-border opacity-50" : "border-admin-card-border"}`}>
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-3.5 w-3.5 text-admin-text-subtle shrink-0" />
                        <Input
                          value={String(alt.text ?? "")}
                          onChange={(e) => updateAltField(idx, "text", e.target.value)}
                          placeholder="Alternativtekst"
                          className={`flex-1 h-7 text-sm ${alt.isActive === false ? "line-through text-admin-text-subtle" : ""}`}
                          disabled={alt.isActive === false}
                        />
                        <Input
                          type="number"
                          value={alt.order ?? 0}
                          onChange={(e) => updateAltField(idx, "order", String(parseInt(e.target.value) || 0))}
                          className="w-14 h-7 text-sm"
                          disabled={alt.isActive === false}
                        />
                        <label className="flex items-center gap-1 text-xs cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={alt.isOther ?? false}
                            onChange={(e) => updateAltField(idx, "isOther", e.target.checked)}
                            className="accent-admin-primary"
                            disabled={alt.isActive === false}
                          />
                          Annet
                        </label>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(alt, idx)}
                          className={`h-7 w-7 shrink-0 ${alt.isActive === false ? "text-admin-primary hover:text-admin-primary" : "text-admin-text-subtle hover:text-admin-text-primary"}`}
                          title={alt.isActive === false ? "Aktiver" : "Deaktiver"}
                        >
                          <EyeOff className="h-3 w-3" />
                        </Button>
                        {alt.isNew && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAltEdit(idx)}
                            className="h-7 w-7 shrink-0 text-admin-negative hover:text-admin-negative"
                            title="Fjern"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      {!alt.isOther && (
                        <BiometricPicker
                          selected={toStringArray(alt.recommendedBiometricIds)}
                          onChange={(ids) => updateAltField(idx, "recommendedBiometricIds", ids)}
                          biometrics={biometrics}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    <Save className="h-3 w-3" />
                    Lagre
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Avbryt</Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1 text-sm">
                  <p className="text-admin-text-secondary">{question.description}</p>
                  {question.tags && (
                    <div className="flex gap-1">
                      {String(question.tags).split(",").map((t) => (
                        <Badge key={t.trim()} variant="outline" className="text-[10px]">{t.trim()}</Badge>
                      ))}
                    </div>
                  )}
                  {question.secondaryQuestion && (
                    <p className="text-admin-text-subtle text-xs">Oppfolging: {question.secondaryQuestion}</p>
                  )}
                </div>

                {/* Read-only numeric rules */}
                {numericRules.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-admin-text-subtle flex items-center gap-1.5">
                      <Calculator className="h-3.5 w-3.5" />
                      Numeriske regler
                    </label>
                    {numericRules.map((rule) => {
                      const ruleIds = toStringArray(rule.recommendedBiometricIds);
                      return (
                        <div key={rule.id} className="pl-2 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Badge variant="info" className="text-[10px] font-mono">
                              {ARITHMETIC_LABELS[rule.arithmetic] ?? rule.arithmetic} {rule.treshold}
                            </Badge>
                            {ruleIds.length > 0 && (
                              <span className="text-xs text-admin-text-subtle flex items-center gap-1">
                                <FlaskConical className="h-3 w-3" />
                                {ruleIds.length} biomarkorer
                              </span>
                            )}
                          </div>
                          {ruleIds.length > 0 && (
                            <div className="flex flex-wrap gap-1 pl-2">
                              {ruleIds.map((id) => (
                                <Badge key={id} variant="default" className="text-[10px]">{biometricName(id)}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Read-only alternatives with biometric badges */}
                {alternatives.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-admin-text-subtle">Alternativer</label>
                    {alternatives
                      .slice()
                      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
                      .map((alt) => {
                        const altBioIds = toStringArray(alt.recommendedBiometricIds);
                        return (
                          <div key={alt.id} className={`pl-2 space-y-0.5 ${alt.isActive === false ? "opacity-40" : ""}`}>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-admin-text-subtle text-xs w-4">{alt.order}.</span>
                              <span className={alt.isActive === false ? "line-through text-admin-text-subtle" : ""}>{alt.text}</span>
                              {alt.isOther && <Badge variant="outline" className="text-[10px]">Annet</Badge>}
                              {alt.isActive === false && <Badge variant="outline" className="text-[10px] text-admin-text-subtle">Deaktivert</Badge>}
                              {altBioIds.length > 0 && (
                                <span className="text-xs text-admin-text-subtle flex items-center gap-1 ml-auto">
                                  <FlaskConical className="h-3 w-3" />
                                  {altBioIds.length}
                                </span>
                              )}
                            </div>
                            {altBioIds.length > 0 && (
                              <div className="flex flex-wrap gap-1 pl-6">
                                {altBioIds.map((id) => (
                                  <Badge key={id} variant="default" className="text-[10px]">{biometricName(id)}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={startEditing} className="text-xs h-7">
                    Rediger
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleToggleDeprecated}
                    disabled={updateQuestion.isPending}
                    className="text-xs h-7"
                    title={question.isDeprecated ? undefined : "Skjules for kunder som ikke har svart"}
                  >
                    <EyeOff className="h-3 w-3" />
                    {question.isDeprecated ? "Gjenopprett" : "Marker som utgått"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDelete} className="text-admin-negative hover:text-admin-negative text-xs h-7">
                    <Trash2 className="h-3 w-3" />
                    Slett
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
